import _ from "lodash";
import React, { Fragment, useContext, useEffect, useState } from "react";
import { confirmAlert } from "react-confirm-alert";
import DebounceInput from "react-debounce-input";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import {
  Alert as Alert2,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import { DesktopContainer } from "../layouts";
import { api, constants, dateHelpers, UserContext } from "../utils";
import { AttachmentManager, ItemTypeahead, ServiceThreadSpinner } from "./";

const PROCESS_CHANGE_TYPE_NAMES = [
  "Final Approver",
  "Maintenance Engineering",
  "Manufacturing Operations",
  "Quality",
  "Research & Development",
];

function ReviewCol({
  onApprove,
  setRejectingTypeOfProcessChangeApprover,
  approverTypeId,
}) {
  return (
    <Col xs={7}>
      <ButtonGroup>
        <Button
          color="success"
          size="sm"
          children="Approve"
          onClick={() => onApprove(approverTypeId)}
        />
        <Button
          color="danger"
          size="sm"
          children="Reject"
          onClick={() =>
            setRejectingTypeOfProcessChangeApprover(approverTypeId)
          }
        />
      </ButtonGroup>
    </Col>
  );
}

function ReviewStatusOnlyCol({
  item,
  approvalDatePropName,
  approvalByPropName,
  authorizedPrefix,
  rejection,
}) {
  if (rejection) {
    return (
      <Fragment>
        <Col xs={3} className="text-danger">
          Date: {dateHelpers.toLongDateString(rejection.rejectedAt)}
        </Col>
        <Col xs={4} className="text-danger">
          Rejected By: {rejection.reviewedBy}
          <div style={{ fontStyle: "italic", color: "#000" }}>
            {rejection.rejectReason}
          </div>
        </Col>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Col xs={3}>
        {item[approvalDatePropName]
          ? `Date: ${dateHelpers.toLongDateString(item[approvalDatePropName])}`
          : "-"}
      </Col>
      <Col xs={4}>
        {item[approvalDatePropName]
          ? (authorizedPrefix || "Approved By: ") + item[approvalByPropName]
          : "-"}
      </Col>
    </Fragment>
  );
}

export default function ProcessChangeEdit(props) {
  const { routePCRId } = useParams();
  const resolvedPCRId = parseInt(routePCRId.replace("PCR-", ""), 10);
  const { currentUser } = useContext(UserContext);
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [machineTypeList, setMachineTypeList] = useState([]);
  const [message, setMessage] = useState(null);
  const [processTypeList, setProcessTypeList] = useState([]);
  const [rejectingTypeOfProcessChangeApprover, setRejectingTypeOfProcessChangeApprover] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [rejections, setRejections] = useState({});
  const [selectedPart, setSelectedPart] = useState(null);

  useEffect(() => {
    let apiList = [];
    apiList.push(
      api
        .fetch("Reference/GetProcessChangeTypes")
        .then((response) => {
          let list = _.map(response.data.processChangeTypes, (m) => {
            m.value = m.id;
            m.label = m.name;
            return m;
          });
          list.unshift(constants.ALL_ITEM);
          setProcessTypeList(list);
          return { processTypeList: list };
        })
        .catch((error) =>
          setMessage({
            flavor: "alert-danger",
            text: "There was an error loading drop down list data",
          })
        )
    );
    apiList.push(
      api
        .fetch("Reference/GetRepairMachineTypes")
        .then((response) => {
          let list = _.map(response.data.machineTypes, (m) => {
            m.value = m.typeId;
            m.label = m.typeName;
            return m;
          });
          list.unshift(constants.ALL_ITEM);
          setMachineTypeList(list);
          return { machineTypeList: list };
        })
        .catch((error) =>
          setMessage({
            flavor: "alert-danger",
            text: "There was an error loading drop down list data",
          })
        )
    );
    Promise.all(apiList).then((aggResults) => {
      let lists = {};
      _.each(aggResults, (x) => (lists = Object.assign(lists, x)));
      if (resolvedPCRId) {
        setLoading(true);
        api
          .fetch(`Reference/GetProcessChange/${resolvedPCRId}`)
          .then((r) => {
            if (r.data.success) {
              let newItem = r.data.message.pcr;
              newItem.createdDate = dateHelpers.formatDateForServer(
                new Date(newItem.createdDate)
              );
              // newItem.createdAt = moment(newItem.createdDate).format(dateHelpers.MDYHMA);
              newItem.savingType = _.find(
                constants.SAVINGS_TYPES,
                (st) => st.value === newItem.savingType
              );
              newItem.itemTypeId = _.find(
                constants.PART_NUMBER_TYPES,
                (pn) => pn.value === newItem.itemTypeId
              );
              newItem.processType = _.find(
                lists.processTypeList,
                (x) => x.value === newItem.processType
              );
              if (newItem.machineTypeId) {
                newItem.machineType = _.find(
                  lists.machineTypeList,
                  (x) => x.value === newItem.machineTypeId
                );
              }
              newItem.partList = newItem.itemList;
              setItem(newItem);
              // setSignatureList(r.data.message.signatureList);
              let rejectionObj = {};
              _.each(
                [
                  constants.PROCESS_CHANGE_APPROVER_TYPE_IDS.FinalApprover,
                  constants.PROCESS_CHANGE_APPROVER_TYPE_IDS
                    .MaintenanceEngineering,
                  constants.PROCESS_CHANGE_APPROVER_TYPE_IDS
                    .ManufacturingOperations,
                  constants.PROCESS_CHANGE_APPROVER_TYPE_IDS.Quality,
                  constants.PROCESS_CHANGE_APPROVER_TYPE_IDS.Research,
                ],
                (reviewTypeId) => {
                  const rj = _.find(
                    r.data.message.signatureList,
                    (s) =>
                      s.typeOfProcessChangeApprover === reviewTypeId &&
                      s.rejectedAt
                  );
                  if (rj) {
                    rejectionObj["REJECTION" + reviewTypeId] = {
                      rejectedAt: dateHelpers.momentFromString(rj.rejectedAt),
                      reviewedBy: rj.reviewedBy,
                      rejectReason: rj.rejectReason,
                    };
                  }
                }
              );
              setRejections(rejectionObj);
            } else {
              toast.error(r.data.message);
            }
          })
          .catch(api.catchHandler)
          .finally(() => setLoading(false));
      } else {
        setItem({
          createdDate: dateHelpers.formatDateForServer(new Date()),
          createdBy: currentUser.firstName + " " + currentUser.lastName,
          savingType: constants.SAVINGS_TYPES.ONE_TIME,
          partList: [],
          expectedSaving: "",
          processType: "",
          machineType: "",
          itemTypeId: constants.PART_NUMBER_TYPES.NA,
          affectedProcess: "",
          changeDescription: "",
          expectedBenefit: "",
          id: 0,
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedPCRId]);

  function createPayload() {
    let partNumberCSV = "";
    if (item.partList && item.partList.length) {
      partNumberCSV = _.map(item.partList, (p) => p.itemNumber).join(",");
    }
    return {
      id: parseInt(item.id, 10),
      expectedSaving: parseInt(item.expectedSaving, 10),
      processType: item.processType.value,
      affectedProcess: item.affectedProcess,
      expectedBenefit: item.expectedBenefit,
      changeDescription: item.changeDescription,
      savingType: item.savingType.value,
      itemTypeId: item.itemTypeId.value,
      machineTypeId: item.machineType ? item.machineType.value : null,
      isActive: true,
      itemNumbers: partNumberCSV,
      createdBy: item.createdBy || "-",
      createdDate: item.createdDate || new Date(),
    };
  }

  function currentUserIs(processChangeApproverTypeId) {
    return currentUser.approverRoleTypeIdList.includes(
      processChangeApproverTypeId
    );
  }

  function getValidationWarnings() {
    let warnings = [];
    if (!parseInt(item.expectedSaving, 10)) {
      warnings.push("Provide an expected savings in the format (999,999)");
    }
    if (!item.savingType) {
      warnings.push("Savings type is required");
    }
    if (!item.processType) {
      warnings.push("Process type is required");
    }
    if (!item.affectedProcess) {
      warnings.push("Current method is required");
    }
    if (!item.expectedBenefit) {
      warnings.push("Expected benefit is required");
    }
    if (!item.changeDescription) {
      warnings.push("Description is required");
    }
    if (!item.itemTypeId) {
      warnings.push("Item type is required");
    } else if (
      !item.machineType &&
      item.itemTypeId.value === constants.PART_NUMBER_TYPES.NA.value
    ) {
      const LOOSE_PROCESSES = [
        constants.PROCESS_CHANGE_TYPE_IDS.Material_Handling_and_Storage,
        constants.PROCESS_CHANGE_TYPE_IDS.Product_Packaging,
        constants.PROCESS_CHANGE_TYPE_IDS.QC_Inspection,
      ];
      if (!item.processType || !LOOSE_PROCESSES.includes(item.processType)) {
        warnings.push(
          "You must choose either a machine type or an item number to continue."
        );
      }
    }
    return warnings;
  }

  function onAddPart() {
    if (!selectedPart) return;
    let newList = item.partList.slice();
    newList.push(selectedPart);
    setSelectedPart(null);
    onChange("partList", newList);
  }

  function onApprove(pTypeOfProcessChangeApprover) {
    confirmAlert({
      title: "Confirm Save",
      message: `${PROCESS_CHANGE_TYPE_NAMES[pTypeOfProcessChangeApprover]} approval will also save any pending changes to this form. Continue?`,
      buttons: [
        {
          label: "Yes",
          onClick: () => {
            let payload = createPayload();
            payload.TypeOfProcessChangeApprover = pTypeOfProcessChangeApprover;
            api
              .post("Reference/ApproveProcessChange", payload)
              .then((r) => {
                if (r.data.success) {
                  navigate(constants.ENDPOINTS.processChanges);
                } else {
                  setMessage({
                    flavor: "alert-warning",
                    text: r.data.message,
                  });
                }
              })
              .catch(api.catchHandler);
          },
        },
        {
          label: "No",
        },
      ],
    });
  }

  function onChange(field, value) {
    setItem({
      ...item,
      [field]: value,
    });
  }

  function onReject() {
    if (rejectionReason) {
      setMessage(null);
    } else {
      setMessage({ flavor: "warning", text: "A rejection reason is required" });
      return;
    }
    let payload = createPayload();
    payload.TypeOfProcessChangeApprover = rejectingTypeOfProcessChangeApprover;
    payload.Reason = rejectionReason;
    api
      .post("Reference/RejectProcessChange", payload)
      .then((r) => {
        if (r.data.success) {
          setRejectingTypeOfProcessChangeApprover(null);
          navigate(constants.ENDPOINTS.processChanges);
        } else {
          setMessage({
            flavor: "alert-warning",
            text: r.data.message,
          });
        }
      })
      .catch(api.catchHandler);
  }

  function onSave() {
    const warnings = getValidationWarnings();
    if (warnings.length) {
      setMessage({ flavor: "warning", text: warnings.join(", ") });
    } else {
      setMessage(null);
    }
    setLoading(true);
    api
      .post("Reference/SaveProcessChangeRequest", createPayload())
      .then((r) => {
        if (r.data.success) {
          navigate(constants.ENDPOINTS.processChanges);
        } else {
          setMessage({
            flavor: "alert-warning",
            text: r.data.message,
          });
        }
      })
      .catch(api.catchHandler)
      .finally(() => setLoading(false));
  }

  return (
    <DesktopContainer screenName="Process Change Edit">
      <ServiceThreadSpinner on={loading || !item}>
        {message ? (
          <Row className="mt-3">
            <Col>
              <Alert2 className={message.flavor}>{message.text}</Alert2>
            </Col>
          </Row>
        ) : null}
        {rejectingTypeOfProcessChangeApprover ? (
          <Modal isOpen>
            <ModalHeader children="Provide Rejection Reason" />
            <ModalBody>
              Rejection Reason
              <DebounceInput
                className="serviceThreadTextArea w-100"
                element="textarea"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                makeAnimated={makeAnimated}
                debounceTimeout={300}
                maxLength={500}
              />
            </ModalBody>
            <ModalFooter>
              <ButtonGroup>
                <Button
                  color="primary"
                  disabled={!rejectionReason}
                  children="Reject"
                  onClick={onReject}
                />
                <Button
                  color="secondary"
                  children="Cancel"
                  onClick={() => setRejectingTypeOfProcessChangeApprover(null)}
                />
              </ButtonGroup>
            </ModalFooter>
          </Modal>
        ) : null}
        {item ? (
          <Row>
            <Col xs={{ size: 8, offset: 2 }}>
              <Row>
                <Col xs={12}>
                  <Row style={{ borderBottom: "solid 1px #999" }}>
                    <Col xs={4}>
                      <Label className="data-prompt">PCI Number:</Label>{" "}
                      {item.displayId || "Determined upon save"}
                    </Col>
                    <Col xs={4}>
                      <Label className="data-prompt">Date:</Label>{" "}
                      {item.createdDate}
                    </Col>
                    <Col xs={4}>
                      <Label className="data-prompt">Initiated By:</Label>{" "}
                      {item.createdBy}
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Row>
                <Col xs={12}>
                  <Row className="align-items-center my-1">
                    <Col xs={3}>
                      <Label className="data-prompt">Savings Type:</Label>
                    </Col>
                    <Col xs={3}>
                      <Row>
                        {_.map(constants.SAVINGS_TYPES, (st) => (
                          <Col key={st.value}>
                            <input
                              type="radio"
                              name="savingType"
                              checked={
                                item.savingType &&
                                st.value === item.savingType.value
                              }
                              onChange={() => onChange("savingType", st)}
                            />{" "}
                            {st.label}
                          </Col>
                        ))}
                      </Row>
                    </Col>
                    <Col xs={3}>
                      <Label className="data-prompt float-end">
                        Expected Savings:
                      </Label>
                    </Col>
                    <Col xs={3}>
                      <Input
                        style={{ width: "130px", textAlign: "right" }}
                        value={item.expectedSaving}
                        onChange={(e) =>
                          onChange("expectedSaving", e.target.value)
                        }
                      />
                    </Col>
                  </Row>
                  <Row className="align-items-center my-1">
                    <Col xs={3}>
                      <Label className="data-prompt">Process Type:</Label>
                    </Col>
                    <Col xs={6}>
                      <Select
                        classNamePrefix="serviceThreadSelect"
                        closeMenuOnSelect={true}
                        components={makeAnimated()}
                        options={processTypeList}
                        onChange={(e) => onChange("processType", e)}
                        value={item.processType}
                      />
                    </Col>
                  </Row>
                  <Row className="align-items-center my-1">
                    <Col xs={3}>
                      <Label className="data-prompt">Machine Type:</Label>
                    </Col>
                    <Col xs={6}>
                      <Select
                        classNamePrefix="serviceThreadSelect"
                        closeMenuOnSelect={true}
                        components={makeAnimated()}
                        options={machineTypeList}
                        onChange={(e) => onChange("machineType", e)}
                        value={item.machineType}
                      />
                    </Col>
                  </Row>
                  <Row className="align-items-center my-1">
                    <Col xs={3}>
                      <Label className="data-prompt">Part Number(s):</Label>
                    </Col>
                    <Col xs={9}>
                      <Row>
                        {_.map(constants.PART_NUMBER_TYPES, (pn) => (
                          <Col key={pn.value}>
                            <input
                              type="radio"
                              name="itemTypeId"
                              checked={
                                item.itemTypeId &&
                                pn.value === item.itemTypeId.value
                              }
                              onChange={() => onChange("itemTypeId", pn)}
                            />{" "}
                            {pn.label}
                          </Col>
                        ))}
                      </Row>
                    </Col>
                  </Row>
                  {item.itemTypeId &&
                  item.itemTypeId.value ===
                    constants.PART_NUMBER_TYPES.SELECTED.value ? (
                    <Row className="align-top my-1">
                      <Col xs={8}>
                        <ItemTypeahead
                          value={selectedPart}
                          isClearable
                          onChange={setSelectedPart}
                        />
                        <div>
                          <div className="data-prompt">Selected Items:</div>
                          {item.partList && item.partList.length
                            ? _.map(item.partList, (p) => (
                                <div
                                  className="m-1 ps-3"
                                  key={p.itemNumber}
                                  style={{ fontWeight: "bold" }}
                                >
                                  {p.itemWithDesc}
                                </div>
                              ))
                            : "None"}
                        </div>
                      </Col>
                      <Col xs={4}>
                        {item.partList.length >= 3 ? null : (
                          <Button
                            className="shredButton"
                            children="Include"
                            onClick={onAddPart}
                          />
                        )}
                      </Col>
                    </Row>
                  ) : null}
                  <Row className="align-top my-2">
                    <Col xs={3}>
                      <Label className="data-prompt">Current Method:</Label>
                    </Col>
                    <Col xs={9}>
                      <DebounceInput
                        className="serviceThreadTextArea"
                        element="textarea"
                        style={{ height: "150px", width: "100%" }}
                        value={item.affectedProcess}
                        maxLength={500}
                        onChange={(e) =>
                          onChange("affectedProcess", e.target.value)
                        }
                        debounceTimeout={300}
                      />
                    </Col>
                  </Row>
                  <Row className="align-top my-2">
                    <Col xs={3}>
                      <Label className="data-prompt">
                        Description of Change:
                      </Label>
                    </Col>
                    <Col xs={9}>
                      <DebounceInput
                        className="serviceThreadTextArea"
                        element="textarea"
                        style={{ height: "150px", width: "100%" }}
                        value={item.changeDescription}
                        maxLength={500}
                        onChange={(e) =>
                          onChange("changeDescription", e.target.value)
                        }
                        debounceTimeout={300}
                      />
                    </Col>
                  </Row>
                  <Row className="align-top my-2">
                    <Col xs={3}>
                      <Label className="data-prompt">Expected Benefits:</Label>
                    </Col>
                    <Col xs={9}>
                      <DebounceInput
                        className="serviceThreadTextArea"
                        element="textarea"
                        style={{ height: "150px", width: "100%" }}
                        value={item.expectedBenefit}
                        maxLength={500}
                        onChange={(e) =>
                          onChange("expectedBenefit", e.target.value)
                        }
                        debounceTimeout={300}
                      />
                    </Col>
                  </Row>
                  {item.id !== 0 && (
                    <Fragment>
                      <Row>
                        <Col xs={12}>
                          <Row style={{ borderBottom: "solid 1px #999" }}>
                            <Col xs={12}>
                              <h3 className="primaryDarkText">Attachments</h3>
                            </Col>
                          </Row>
                          <Row>
                            <Col>
                              <Card className="dashboard-card">
                                <CardBody>
                                  <AttachmentManager
                                    attachmentTypeId={
                                      constants.ATTACHMENT_TYPES
                                        .ProcessChangeRequest
                                    }
                                    contextName={item.displayId}
                                    attachmentKey={item.displayId}
                                  />
                                </CardBody>
                              </Card>
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={12}>
                          <Row style={{ borderBottom: "solid 1px #999" }}>
                            <Col xs={12}>
                              <h3 className="primaryDarkText">
                                Review &amp; Approval
                              </h3>
                            </Col>
                          </Row>
                          <Row className="pt-2" style={{ height: "30px" }}>
                            <Col xs={5}>Maintenance Engineering</Col>
                            {!item.maintDate &&
                            currentUserIs(
                              constants.PROCESS_CHANGE_APPROVER_TYPE_IDS
                                .MaintenanceEngineering
                            ) ? (
                              <ReviewCol
                                onApprove={onApprove}
                                setRejectingTypeOfProcessChangeApprover={
                                  setRejectingTypeOfProcessChangeApprover
                                }
                                approverTypeId={
                                  constants.PROCESS_CHANGE_APPROVER_TYPE_IDS
                                    .MaintenanceEngineering
                                }
                              />
                            ) : (
                              <ReviewStatusOnlyCol
                                item={item}
                                approvalDatePropName="maintDate"
                                approvalByPropName="maintApprover"
                              />
                            )}
                          </Row>
                          <Row className="pt-2" style={{ height: "30px" }}>
                            <Col xs={5}>Manufacturing Operations</Col>
                            {!item.mfgDate &&
                            currentUserIs(
                              constants.PROCESS_CHANGE_APPROVER_TYPE_IDS
                                .ManufacturingOperations
                            ) ? (
                              <ReviewCol
                                onApprove={onApprove}
                                setRejectingTypeOfProcessChangeApprover={
                                  setRejectingTypeOfProcessChangeApprover
                                }
                                approverTypeId={
                                  constants.PROCESS_CHANGE_APPROVER_TYPE_IDS
                                    .ManufacturingOperations
                                }
                              />
                            ) : (
                              <ReviewStatusOnlyCol
                                item={item}
                                approvalDatePropName="mfgDate"
                                approvalByPropName="mfgApprover"
                              />
                            )}
                          </Row>
                          <Row className="pt-2" style={{ height: "30px" }}>
                            <Col xs={5}>Quality</Col>
                            {!item.qualityDate &&
                            currentUserIs(
                              constants.PROCESS_CHANGE_APPROVER_TYPE_IDS.Quality
                            ) ? (
                              <ReviewCol
                                onApprove={onApprove}
                                setRejectingTypeOfProcessChangeApprover={
                                  setRejectingTypeOfProcessChangeApprover
                                }
                                approverTypeId={
                                  constants.PROCESS_CHANGE_APPROVER_TYPE_IDS
                                    .Quality
                                }
                              />
                            ) : (
                              <ReviewStatusOnlyCol
                                item={item}
                                approvalDatePropName="qualityDate"
                                approvalByPropName="qualityApprover"
                              />
                            )}
                          </Row>
                          <Row className="pt-2" style={{ height: "30px" }}>
                            <Col xs={5}>Research &amp; Development</Col>
                            {!item.researchDate &&
                            !rejections[
                              "REJECTION" +
                                constants.PROCESS_CHANGE_APPROVER_TYPE_IDS
                                  .Research
                            ] &&
                            currentUserIs(
                              constants.PROCESS_CHANGE_APPROVER_TYPE_IDS
                                .Research
                            ) ? (
                              <ReviewCol
                                onApprove={onApprove}
                                setRejectingTypeOfProcessChangeApprover={
                                  setRejectingTypeOfProcessChangeApprover
                                }
                                approverTypeId={
                                  constants.PROCESS_CHANGE_APPROVER_TYPE_IDS
                                    .Research
                                }
                              />
                            ) : (
                              <ReviewStatusOnlyCol
                                item={item}
                                rejection={
                                  rejections[
                                    "REJECTION" +
                                      constants.PROCESS_CHANGE_APPROVER_TYPE_IDS
                                        .Research
                                  ]
                                }
                                approvalDatePropName="researchDate"
                                approvalByPropName="researchApprover"
                              />
                            )}
                          </Row>
                        </Col>
                      </Row>
                      <Row className="pt-2 mt-3">
                        <Col xs={12}>
                          <Row style={{ borderBottom: "solid 1px #999" }}>
                            <Col xs={12}>
                              <h3 className="primaryDarkText">
                                Authorization to Implement Change
                              </h3>
                            </Col>
                          </Row>
                          <Row className="pt-2" style={{ height: "30px" }}>
                            <Col xs={5}>Final Approval</Col>
                            {!item.approvedDate &&
                            currentUserIs(
                              constants.PROCESS_CHANGE_APPROVER_TYPE_IDS
                                .FinalApprover
                            ) ? (
                              <ReviewCol
                                onApprove={onApprove}
                                setRejectingTypeOfProcessChangeApprover={
                                  setRejectingTypeOfProcessChangeApprover
                                }
                                approverTypeId={
                                  constants.PROCESS_CHANGE_APPROVER_TYPE_IDS
                                    .FinalApprover
                                }
                              />
                            ) : (
                              <ReviewStatusOnlyCol
                                item={item}
                                approvalDatePropName="approvedDate"
                                approvalByPropName="approvedBy"
                                authorizedPrefix="Authorized By"
                              />
                            )}
                          </Row>
                        </Col>
                      </Row>
                    </Fragment>
                  )}
                  <Row className="align-items-center my-1">
                    <Col xs={12}>
                      <ButtonGroup className="float-end">
                        {!_.isNil(item) ? (
                          <Button
                            className="shredButton"
                            children="SAVE"
                            onClick={onSave}
                          />
                        ) : null}
                        <Button
                          tag={Link}
                          children="CANCEL"
                          to={constants.ENDPOINTS.processChanges}
                        />
                      </ButtonGroup>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Col>
          </Row>
        ) : null}
      </ServiceThreadSpinner>
    </DesktopContainer>
  );
}
